import { createRootRoute, createRoute, createRouter, Link } from '@tanstack/react-router';
import { AppRoot } from './App';
import {
  DocumentsPage,
  GoalsPage,
  PeoplePage,
  ProfilePage,
  ReviewsPage,
  OrganisationChartPage,
} from './pages';
import { CreateResourcePage, ResourceDetailPage, ResourceActionPage } from './resource-routes';
import { DomainPage, OverviewPage } from './hr-pages';
import { EmployeeActionPage } from './employee-routes';
import { EmployeeBulkPage, EmployeeImportPage } from './employee-operations';
import { AccountSettings } from './account-settings';
import { AppearancePage } from './account-appearance';
import { ReviewSubmissionPage, ReviewReturnPage } from './review-pages';
import { MfaEnrollmentPage, NotificationSettingsPage } from './security-pages';
import { OperationsPage, BulkOperationPage, WorkspaceSettingsPage } from './operations-pages';
import { CandidateMergePage } from './candidate-merge';
import { AdministrativePage } from './administrative';
const root = createRootRoute({
  component: AppRoot,
  notFoundComponent: () => (
    <section className="panel empty-state">
      <h2>Страница не найдена</h2>
      <p className="muted">Проверьте адрес или вернитесь к обзору.</p>
      <Link to="/" className="pill">
        Открыть обзор
      </Link>
    </section>
  ),
});
type ResourceSearch = {
  employeeId?: string;
  versionId?: string;
  taskId?: string;
  tab?: string;
  parentId?: string;
  childId?: string;
  childKey?: string;
};
const resourceSearch = (search: Record<string, unknown>): ResourceSearch =>
  Object.fromEntries(
    ['employeeId', 'versionId', 'taskId', 'tab', 'parentId', 'childId', 'childKey']
      .filter((key) => typeof search[key] === 'string')
      .map((key) => [key, search[key]]),
  );
const administrativeSearch = (search: Record<string, unknown>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(search).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
const routes = [
  createRoute({
    getParentRoute: () => root,
    path: '/administrative',
    component: AdministrativePage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/administrative/$page',
    component: AdministrativePage,
    validateSearch: administrativeSearch,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/administrative/$page/$entityId',
    component: AdministrativePage,
    validateSearch: administrativeSearch,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/administrative/$page/$entityId/$operation',
    component: AdministrativePage,
    validateSearch: administrativeSearch,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/recruitment/candidates/merge',
    component: CandidateMergePage,
  }),
  createRoute({ getParentRoute: () => root, path: '/careers', component: OverviewPage }),
  createRoute({ getParentRoute: () => root, path: '/careers/$publicId', component: OverviewPage }),
  createRoute({
    getParentRoute: () => root,
    path: '/careers/$publicId/apply',
    component: OverviewPage,
  }),
  createRoute({ getParentRoute: () => root, path: '/offer', component: OverviewPage }),
  createRoute({ getParentRoute: () => root, path: '/withdraw-consent', component: OverviewPage }),
  createRoute({ getParentRoute: () => root, path: '/operations/$mode', component: OperationsPage }),
  createRoute({
    getParentRoute: () => root,
    path: '/operations/result/$operationId',
    component: BulkOperationPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/admin/workspace',
    component: WorkspaceSettingsPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/admin/workspace/edit',
    component: WorkspaceSettingsPage,
  }),
  createRoute({ getParentRoute: () => root, path: '/', component: OverviewPage }),
  createRoute({ getParentRoute: () => root, path: '/activate', component: OverviewPage }),
  createRoute({ getParentRoute: () => root, path: '/forgot-password', component: OverviewPage }),
  createRoute({ getParentRoute: () => root, path: '/reset-password', component: OverviewPage }),
  createRoute({
    getParentRoute: () => root,
    path: '/account/appearance',
    component: AppearancePage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/account/notifications',
    component: NotificationSettingsPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/account/security/$method',
    component: MfaEnrollmentPage,
  }),
  createRoute({ getParentRoute: () => root, path: '/my-profile', component: ProfilePage }),
  createRoute({
    getParentRoute: () => root,
    path: '/employees/profile/$employeeId',
    component: ProfilePage,
    validateSearch: (search: Record<string, unknown>): { tab?: string } =>
      typeof search.tab === 'string' ? { tab: search.tab } : {},
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/employees/profile/$employeeId/$operation',
    component: EmployeeActionPage,
    validateSearch: (search: Record<string, unknown>): { kind?: string; versionId?: string } => ({
      kind: typeof search.kind === 'string' ? search.kind : undefined,
      versionId: typeof search.versionId === 'string' ? search.versionId : undefined,
    }),
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/account/settings',
    component: AccountSettings,
  }),
  createRoute({ getParentRoute: () => root, path: '/people/employees', component: PeoplePage }),
  createRoute({
    getParentRoute: () => root,
    path: '/people/employees/import',
    component: EmployeeImportPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/people/employees/bulk',
    component: EmployeeBulkPage,
    validateSearch: (search: Record<string, unknown>) => ({
      employees: typeof search.employees === 'string' ? search.employees : undefined,
    }),
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/organisation/chart',
    component: OrganisationChartPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/organisation/teams/departments',
    component: () => <DomainPage section="organisation" resource="departments" />,
    validateSearch: (search: Record<string, unknown>) => ({
      organizationId: typeof search.organizationId === 'string' ? search.organizationId : undefined,
    }),
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/performance/goals/general',
    component: GoalsPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/performance/reviews/general',
    component: ReviewsPage,
  }),
  createRoute({ getParentRoute: () => root, path: '/people/documents', component: DocumentsPage }),
  createRoute({
    getParentRoute: () => root,
    path: '/performance/reviews/$reviewId/submission/$type',
    component: ReviewSubmissionPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/performance/reviews/$reviewId/submission/$type/return',
    component: ReviewReturnPage,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/$section/$resource/new',
    component: CreateResourcePage,
    validateSearch: resourceSearch,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/$section/$resource/$entityId',
    component: ResourceDetailPage,
    validateSearch: resourceSearch,
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/$section/$resource/$entityId/$operation',
    component: ResourceActionPage,
    validateSearch: resourceSearch,
  }),
  createRoute({ getParentRoute: () => root, path: '/$section/$resource', component: DomainPage }),
];
export const router = createRouter({
  routeTree: root.addChildren(routes),
  defaultPreload: 'intent',
  scrollRestoration: true,
});
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
